import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';
import { useI18n } from '../i18n/i18n';

interface MonthlyCostWidgetProps {
  hideHeader?: boolean;
}

const MonthlyCostWidget: React.FC<MonthlyCostWidgetProps> = ({ hideHeader = false }) => {
  const { t } = useI18n();

  const data = [
    {
      name: t('monthlyCost.energy'),
      value: 2857.32,
      color: '#3B82F6' // blue-500
    },
    {
      name: t('monthlyCost.demand'),
      value: 1282.60,
      color: '#8B5CF6' // violet-500
    },
    {
      name: t('monthlyCost.additionalCharge'),
      value: 320.60,
      color: '#10B981' // emerald-500
    }
  ];

  const total = data.reduce((sum, item) => sum + item.value, 0);

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-medium text-gray-900 whitespace-pre-line">{item.name}</p>
          <p className="text-lg font-bold" style={{ color: item.color }}>
            {formatCurrency(item.value)} {t('monthlyCost.currency')}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="h-full flex flex-col bg-white rounded-lg">
      {!hideHeader && (
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">
            {t('monthlyCost.title')}
          </h3>
          <p className="text-xs text-gray-500">{t('monthlyCost.subtitle')}</p>
        </div>
      )}

      <div className="flex-1 p-4 flex flex-col">
        {/* Title inside chart area when header is hidden */}
        {hideHeader && (
          <div className="mb-2">
            <h3 className="text-sm font-semibold text-gray-900">
              {t('monthlyCost.title')}
            </h3>
            <p className="text-xs text-gray-500">{t('monthlyCost.subtitle')}</p>
          </div>
        )}

        {/* Chart */}
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: '#6B7280' }}
                tickLine={{ stroke: '#D1D5DB' }}
                axisLine={{ stroke: '#D1D5DB' }}
                interval={0}
                angle={0}
                textAnchor="middle"
                height={60}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#6B7280' }}
                tickLine={{ stroke: '#D1D5DB' }}
                axisLine={{ stroke: '#D1D5DB' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
                label={{
                  value: t('monthlyCost.currency'),
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fontSize: 11, fill: '#6B7280' }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={80}>
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                <LabelList
                  dataKey="value"
                  position="top"
                  formatter={(value: number) => formatCurrency(value)}
                  style={{ fontSize: 12, fontWeight: 600, fill: '#374151' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Total */}
        <div className="mt-2 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">{t('monthlyCost.total')}</span>
            <span className="text-lg font-bold text-gray-900">
              {formatCurrency(total)} {t('monthlyCost.currency')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyCostWidget;
